"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HttpClient = void 0;
const shared_types_1 = require("@restor/shared-types");
const errors_1 = require("./errors");
const token_store_1 = require("./token-store");
/** Refresh a few seconds early so a token cannot expire mid-flight. */
const REFRESH_SKEW_MS = 10_000;
/**
 * Transport shared by every RESTOR client (TZ §45).
 *
 * Responsibilities kept in one place so no app re-implements them:
 *  - attaches the bearer token and refreshes it exactly once per expiry,
 *  - unwraps the `{ success, data, error }` envelope,
 *  - turns every failure into a {@link RestorApiError},
 *  - enforces a timeout.
 */
class HttpClient {
    baseUrl;
    tokenStore;
    timeoutMs;
    defaultHeaders;
    fetchImpl;
    onUnauthorized;
    onResponse;
    /**
     * In-flight refresh. Concurrent 401s await this single promise instead of
     * each firing their own refresh — otherwise the first rotation invalidates
     * the token the others are still trying to use, and the user is logged out.
     */
    refreshPromise = null;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/+$/, '');
        this.tokenStore = options.tokenStore ?? new token_store_1.MemoryTokenStore();
        this.timeoutMs = options.timeoutMs ?? 20_000;
        this.defaultHeaders = options.defaultHeaders ?? {};
        this.onUnauthorized = options.onUnauthorized;
        this.onResponse = options.onResponse;
        const platformFetch = options.fetchImpl ?? globalThis.fetch;
        if (!platformFetch) {
            throw new Error('No fetch implementation available — pass `fetchImpl`.');
        }
        // Bind so the browser's fetch keeps its `window` receiver.
        this.fetchImpl = platformFetch.bind(globalThis);
    }
    get tokens() {
        return this.tokenStore;
    }
    async setTokens(tokens) {
        await this.tokenStore.set((0, token_store_1.toStoredTokens)(tokens));
    }
    async clearTokens() {
        await this.tokenStore.clear();
    }
    async isAuthenticated() {
        const stored = await this.tokenStore.get();
        return Boolean(stored?.refreshToken);
    }
    get(path, options) {
        return this.request('GET', path, undefined, options);
    }
    /**
     * A list endpoint, put back together into the shape its type promises.
     *
     * The server splits a page across the envelope (TZ §43): the rows go in
     * `data` and the paging counters in `meta.pagination`, so that a caller
     * reading a list never has to reach through two levels. The cost is that a
     * plain `get<Paginated<T>>` returns a bare array while TypeScript believes
     * it has `{ items, pagination }` — `.items` is then `undefined` at runtime
     * and every list silently renders empty. Rejoining the two halves here is
     * what makes the declared type true.
     */
    async getPaginated(path, options) {
        let meta;
        const items = await this.request('GET', path, undefined, {
            ...options,
            onMeta: (received) => {
                meta = received;
            },
        });
        const rows = items ?? [];
        return {
            items: rows,
            // A server that sent no pagination block still gets a usable page rather
            // than `undefined` counters leaking into the UI.
            pagination: meta?.pagination ?? {
                page: 1,
                limit: rows.length,
                total: rows.length,
                totalPages: 1,
                hasNext: false,
                hasPrev: false,
            },
        };
    }
    post(path, body, options) {
        return this.request('POST', path, body, options);
    }
    patch(path, body, options) {
        return this.request('PATCH', path, body, options);
    }
    put(path, body, options) {
        return this.request('PUT', path, body, options);
    }
    delete(path, options) {
        return this.request('DELETE', path, undefined, options);
    }
    /** Multipart upload (product images) — the body must not be JSON-encoded. */
    async upload(path, form, options) {
        return this.request('POST', path, form, options);
    }
    async request(method, path, body, options = {}, isRetry = false) {
        const url = this.buildUrl(path, options.query);
        const startedAt = Date.now();
        const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
        const headers = {
            Accept: 'application/json',
            ...this.defaultHeaders,
            ...options.headers,
        };
        // Let the runtime set the multipart boundary itself.
        if (body !== undefined && !isFormData) {
            headers['Content-Type'] = 'application/json';
        }
        if (options.idempotencyKey) {
            headers['Idempotency-Key'] = options.idempotencyKey;
        }
        if (!options.skipAuth) {
            const accessToken = await this.getValidAccessToken();
            if (accessToken)
                headers.Authorization = `Bearer ${accessToken}`;
        }
        const timeoutMs = options.timeoutMs ?? this.timeoutMs;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        const abortExternally = () => controller.abort();
        options.signal?.addEventListener('abort', abortExternally);
        let response;
        try {
            response = await this.fetchImpl(url, {
                method,
                headers,
                body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
                signal: controller.signal,
            });
        }
        catch (cause) {
            if (controller.signal.aborted && !options.signal?.aborted) {
                throw errors_1.RestorApiError.timeout(timeoutMs);
            }
            throw errors_1.RestorApiError.network(cause);
        }
        finally {
            clearTimeout(timer);
            options.signal?.removeEventListener('abort', abortExternally);
        }
        this.onResponse?.({ url, status: response.status, durationMs: Date.now() - startedAt });
        // A 401 on a non-auth call means the access token died: refresh once, then
        // replay. `isRetry` stops this from looping.
        if (response.status === 401 && !options.skipAuth && !isRetry) {
            const refreshed = await this.refreshTokens();
            if (refreshed) {
                return this.request(method, path, body, options, true);
            }
            await this.clearTokens();
            this.onUnauthorized?.();
        }
        return this.unwrap(response, options.onMeta);
    }
    async unwrap(response, onMeta) {
        const requestId = response.headers.get('x-request-id') ?? undefined;
        if (response.status === 204) {
            return undefined;
        }
        let payload;
        try {
            payload = (await response.json());
        }
        catch {
            // A proxy or gateway returned HTML/plain text instead of our envelope.
            throw new errors_1.RestorApiError({
                code: response.ok ? 'INVALID_RESPONSE' : 'HTTP_ERROR',
                message: `Unexpected non-JSON response (HTTP ${response.status})`,
                status: response.status,
                requestId,
            });
        }
        if ((0, shared_types_1.isApiSuccess)(payload)) {
            onMeta?.(payload.meta);
            return payload.data;
        }
        throw errors_1.RestorApiError.fromApiError(payload.error ?? { code: 'HTTP_ERROR', message: `HTTP ${response.status}` }, response.status, payload.meta?.requestId ?? requestId);
    }
    /** Returns a token that is still valid, refreshing proactively if needed. */
    async getValidAccessToken() {
        const stored = await this.tokenStore.get();
        if (!stored)
            return null;
        if (stored.expiresAt - REFRESH_SKEW_MS > Date.now()) {
            return stored.accessToken;
        }
        const refreshed = await this.refreshTokens();
        return refreshed?.accessToken ?? null;
    }
    /** Rotates the token pair; concurrent callers share one in-flight request. */
    async refreshTokens() {
        if (this.refreshPromise)
            return this.refreshPromise;
        this.refreshPromise = (async () => {
            const stored = await this.tokenStore.get();
            if (!stored?.refreshToken)
                return null;
            try {
                const response = await this.fetchImpl(`${this.baseUrl}/auth/refresh`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ refreshToken: stored.refreshToken }),
                });
                if (!response.ok)
                    return null;
                const payload = (await response.json());
                if (!(0, shared_types_1.isApiSuccess)(payload))
                    return null;
                const next = (0, token_store_1.toStoredTokens)(payload.data);
                await this.tokenStore.set(next);
                return next;
            }
            catch {
                // Offline: keep the stored refresh token so a later attempt can work.
                return null;
            }
            finally {
                this.refreshPromise = null;
            }
        })();
        return this.refreshPromise;
    }
    buildUrl(path, query) {
        const url = `${this.baseUrl}/${path.replace(/^\/+/, '')}`;
        if (!query)
            return url;
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) {
            if (value === undefined || value === null || value === '')
                continue;
            if (Array.isArray(value)) {
                // Repeat the key so the backend receives a real array.
                for (const entry of value) {
                    if (entry !== undefined && entry !== null)
                        params.append(key, String(entry));
                }
            }
            else {
                params.append(key, String(value));
            }
        }
        const qs = params.toString();
        return qs ? `${url}?${qs}` : url;
    }
}
exports.HttpClient = HttpClient;
//# sourceMappingURL=http-client.js.map